/**
 * PDF Extraction Utility
 * Extracts text + renders pages to base64 images using PDF.js
 * Used for AI CV analysis — frontend-first extraction approach
 */

import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

// Set the worker source (use CDN for the matching version)
pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';

/**
 * Extract text and page images from a PDF file or URL
 *
 * @param {File|string} source - A File object or a URL string pointing to the PDF
 * @param {Object} options
 * @param {number} options.maxPages - Maximum pages to process (default: 10)
 * @param {number} options.imageScale - Scale factor for rendering (default: 1.5 ≈ 150 DPI)
 * @param {number} options.imageQuality - JPEG quality 0-1 (default: 0.75)
 * @returns {Promise<{ extractedText: string, images: string[] }>}
 */
export async function extractPdfContent(source, options = {}) {
  const { maxPages = 10, imageScale = 1.5, imageQuality = 0.75 } = options;

  let pdfData;

  if (source instanceof File || source instanceof Blob) {
    // Read File/Blob into ArrayBuffer
    pdfData = await readFileAsArrayBuffer(source);
  } else if (source instanceof ArrayBuffer) {
    // Already an ArrayBuffer (e.g. from authenticated fetch)
    pdfData = source;
  } else if (typeof source === 'string') {
    // URL string — fetch the PDF
    const response = await fetch(source);
    if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
    pdfData = await response.arrayBuffer();
  } else {
    throw new Error('Source must be a File, Blob, ArrayBuffer, or URL string');
  }

  const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
  const totalPages = Math.min(pdf.numPages, maxPages);

  let extractedText = '';
  const images = [];

  for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
    const page = await pdf.getPage(pageNum);

    // ── Extract text ──
    const textContent = await page.getTextContent();
    const pageText = textContent.items.map(item => item.str).join(' ');
    if (pageText.trim()) {
      extractedText += `--- Page ${pageNum} ---\n${pageText.trim()}\n\n`;
    }

    // ── Render page to image ──
    const viewport = page.getViewport({ scale: imageScale });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d');

    await page.render({ canvasContext: ctx, viewport }).promise;

    const dataUrl = canvas.toDataURL('image/jpeg', imageQuality);
    images.push(dataUrl);

    // Clean up
    canvas.width = 0;
    canvas.height = 0;
  }

  return {
    extractedText: extractedText.trim(),
    images,
  };
}

/**
 * Extract content from an image file (PNG, JPG, etc.)
 * Just converts to base64 data URL — no text extraction possible
 *
 * @param {File} file - Image file
 * @returns {Promise<{ extractedText: string, images: string[] }>}
 */
export async function extractImageContent(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve({
        extractedText: '',
        images: [reader.result], // data:image/...;base64,...
      });
    };
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Helper: read a File/Blob as ArrayBuffer
 */
function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}
